import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import PageWrapper from '@/components/feature/PageWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/data';

const aboutHeroImage = 'https://readdy.ai/api/search-image?query=Korean%20cinema%20film%20archive%20library%20with%20warm%20lighting%2C%20vintage%20movie%20posters%20on%20wall%2C%20elegant%20reading%20room%20with%20film%20books%2C%20sophisticated%20cinema%20culture%20atmosphere%2C%20no%20text%20no%20letters&width=1800&height=600&seq=about-hero&orientation=landscape';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const honeypotValue = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qr5k6e3oq4jqumtpdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 韩影深解 | 韩国电影推荐与深度解析平台"
        description="韩影深解是专注于韩国电影推荐与深度解析的中文平台，提供最全面的韩国电影观影指南、影评分析与推荐榜单。了解我们的品牌故事、口碑评价与联系方式。"
        keywords="关于我们,韩影深解,韩国电影,韩国电影推荐,韩国电影平台"
        canonicalUrl="https://hanyingshenjie.com/about"
      />
      <main>
        <PageWrapper
          heroImage={aboutHeroImage}
          title="关于我们"
          subtitle="致力于成为最专业的韩国电影中文解析平台"
        />

        <div className="w-full bg-background-100 border-b border-background-300/20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                  品牌故事
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  韩影深解创立于2020年，是一群热爱韩国电影的影评人、学者和资深影迷共同打造的专业内容平台。我们相信，好的电影值得被反复品味和深入解读。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  韩国电影在近二十年间经历了令人瞩目的崛起，从戛纳到奥斯卡，从艺术院线到全球流媒体，韩国电影人用独特的叙事语言征服了世界。然而，中文互联网上对韩国电影的深度解读仍然稀缺。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们的使命是填补这一空白——不仅推荐优秀的韩国电影，更要帮助影迷理解每部作品背后的文化语境、社会议题与艺术追求。从奉俊昊的类型片革新到朴赞郁的视觉美学，从金基德的哲思到李沧东的现实洞察，我们将最专业的电影解析带给中文读者。
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20cinema%20culture%20enthusiast%20reading%20film%20books%20in%20warm%20library%2C%20vintage%20movie%20posters%20background%2C%20intellectual%20atmosphere%2C%20warm%20golden%20lighting%2C%20sophisticated%20editorial%20photography&width=600&height=800&seq=about-brand-story&orientation=portrait"
                  alt="韩影深解品牌故事"
                  className="w-full rounded-lg object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-2xl md:text-4xl font-heading font-bold text-accent-500">12+</p>
                <p className="text-xs md:text-sm text-foreground-500 mt-2">精选电影</p>
              </div>
              <div>
                <p className="text-2xl md:text-4xl font-heading font-bold text-accent-500">6</p>
                <p className="text-xs md:text-sm text-foreground-500 mt-2">电影分类</p>
              </div>
              <div>
                <p className="text-2xl md:text-4xl font-heading font-bold text-accent-500">20+</p>
                <p className="text-xs md:text-sm text-foreground-500 mt-2">深度影评</p>
              </div>
              <div>
                <p className="text-2xl md:text-4xl font-heading font-bold text-accent-500">50K+</p>
                <p className="text-xs md:text-sm text-foreground-500 mt-2">月活读者</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-300/30"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                    <p className="text-xs text-foreground-400">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或合作意向，欢迎与我们联系
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5 bg-background-50 rounded-lg p-6 md:p-8 border border-background-300/30"
            >
              <div className="honeypot-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-300/50 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-300/50 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 bg-background-100 border border-background-300/50 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-500 transition-colors"
                  placeholder="消息主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 bg-background-100 border border-background-300/50 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                  placeholder="请输入您的留言（最多500字）"
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                发送消息
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-green-500 text-center">消息已成功发送，我们会尽快回复您！</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 text-center">{formError || '发送失败，请稍后重试'}</p>
              )}
            </form>
          </div>
        </section>
      </main>
    </>
  );
}